import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Title, Text, Button } from '@tremor/react';
import { BookOpenIcon, PlusIcon, TrashIcon, DocumentArrowUpIcon } from '@heroicons/react/24/outline';
import { getTrSources, saveTrSources, uploadTrPdf, removeTrPdf } from '../../Requests/TendencyReportRequests';
import { sanitizeUserInput } from '../../utils/security';
import UpgradedLoader from '../UpgradedLoader';

const MAX_PDF_BYTES = 20 * 1024 * 1024;

/**
 * Step 1: topic / field of study, own links, own PDF reports (text extracted server-side),
 * and the curated trend site list. Topic, links and site picks are saved with "Save";
 * PDFs are saved on upload.
 */
function TrSourcesTab({ projectUuid, analysisUuid, saveRef }) {
    const { t } = useTranslation();
    const [curated, setCurated] = useState([]);
    const [topic, setTopic] = useState('');
    const [selected, setSelected] = useState([]);
    const [customLinks, setCustomLinks] = useState([]);
    const [pdfs, setPdfs] = useState([]);
    const [newLink, setNewLink] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [savedMsg, setSavedMsg] = useState(false);
    const [pdfError, setPdfError] = useState(null);
    const fileRef = useRef(null);

    const applySelection = (sel) => {
        setTopic(sel?.topic ?? '');
        setSelected(sel?.selected_site_urls ?? []);
        setCustomLinks(sel?.custom_links ?? []);
        setPdfs(sel?.pdfs ?? []);
    };

    useEffect(() => {
        getTrSources(projectUuid, analysisUuid)
            .then((res) => {
                setCurated(res.data.curated_sources ?? []);
                applySelection(res.data.selection);
            })
            .finally(() => setLoading(false));
    }, [projectUuid, analysisUuid]);

    const toggle = (url) => setSelected((prev) => (prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]));

    const addLink = () => {
        const clean = sanitizeUserInput(newLink.trim());
        if (clean && !customLinks.includes(clean)) {
            setCustomLinks((prev) => [...prev, clean]);
            setNewLink('');
        }
    };

    const save = () => {
        // A link typed but not yet added still counts.
        const pending = sanitizeUserInput(newLink.trim());
        const links = pending && !customLinks.includes(pending) ? [...customLinks, pending] : customLinks;
        setSaving(true);
        setSavedMsg(false);
        return saveTrSources(projectUuid, analysisUuid, { topic: sanitizeUserInput(topic), selected_site_urls: selected, custom_links: links })
            .then((res) => {
                applySelection(res.data.selection);
                setNewLink('');
                setSavedMsg(true);
                setTimeout(() => setSavedMsg(false), 3000);
            })
            .finally(() => setSaving(false));
    };

    // Let the wizard save on the researcher's behalf when they leave this step without clicking Save.
    useEffect(() => {
        if (saveRef) saveRef.current = save;
    });

    const onPickPdf = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setPdfError(null);
        if (file.size > MAX_PDF_BYTES) {
            setPdfError(t('TendencyReport.Sources.PdfTooLarge'));
            return;
        }
        setUploading(true);
        try {
            const res = await uploadTrPdf(projectUuid, analysisUuid, file);
            setPdfs(res.data.selection?.pdfs ?? []);
        } catch (err) {
            setPdfError(err?.response?.data?.error || t('TendencyReport.Sources.PdfError'));
        } finally {
            setUploading(false);
        }
    };

    const onRemovePdf = async (index) => {
        const res = await removeTrPdf(projectUuid, analysisUuid, index);
        setPdfs(res.data.selection?.pdfs ?? []);
    };

    if (loading) return <UpgradedLoader />;

    return (
        <div data-testid="tr-sources-tab">
            <div className="flex items-center space-x-3 mb-2">
                <BookOpenIcon className="h-6 w-6 text-iavisus-100" />
                <Title className="text-lg font-semibold">{t('TendencyReport.Sources.Title')}</Title>
            </div>
            <Text className="mb-4">{t('TendencyReport.Sources.Description')}</Text>

            {/* Topic */}
            <div className="mb-6">
                <Text className="font-bold mb-2">{t('TendencyReport.Sources.Topic')}</Text>
                <textarea
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    maxLength={2000}
                    rows={3}
                    placeholder={t('TendencyReport.Sources.TopicPlaceholder')}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
            </div>

            {/* Custom links */}
            <div className="mb-6">
                <Text className="font-bold mb-2">{t('TendencyReport.Sources.YourLinks')}</Text>
                <div className="flex gap-2 mb-2">
                    <input
                        type="url"
                        value={newLink}
                        onChange={(e) => setNewLink(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addLink()}
                        placeholder={t('TendencyReport.Sources.CustomLinkPlaceholder')}
                        className="flex-grow border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                    <button type="button" onClick={addLink} className="flex items-center gap-1 border border-iavisus-100 text-iavisus-100 rounded-md px-3 py-2 text-xs font-bold">
                        <PlusIcon className="w-4 h-4" /> {t('TendencyReport.Sources.AddLink')}
                    </button>
                </div>
                {customLinks.map((link) => (
                    <div key={link} className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2 mb-1 text-sm">
                        <span className="truncate">{link}</span>
                        <button type="button" onClick={() => setCustomLinks((p) => p.filter((l) => l !== link))}>
                            <TrashIcon className="w-4 h-4 text-gray-400 hover:text-red-500" />
                        </button>
                    </div>
                ))}
            </div>

            {/* PDF reports */}
            <div className="mb-6">
                <Text className="font-bold mb-2">{t('TendencyReport.Sources.Pdfs')}</Text>
                <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={onPickPdf} data-testid="tr-pdf-input" />
                <Button icon={DocumentArrowUpIcon} loading={uploading} onClick={() => fileRef.current?.click()} className="bg-gray-100 text-gray-700 hover:bg-gray-200 mb-2">
                    {t('TendencyReport.Sources.UploadPdf')}
                </Button>
                {pdfError && <Text className="text-red-600 mb-2">{pdfError}</Text>}
                {pdfs.map((pdf, i) => (
                    <div key={`${pdf.name}-${i}`} className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2 mb-1 text-sm">
                        <span className="truncate">{pdf.name} <span className="text-gray-400">({pdf.chars} {t('TendencyReport.Sources.Chars')})</span></span>
                        <button type="button" onClick={() => onRemovePdf(i)} aria-label={t('TendencyReport.Sources.RemovePdf')}>
                            <TrashIcon className="w-4 h-4 text-gray-400 hover:text-red-500" />
                        </button>
                    </div>
                ))}
            </div>

            {/* Curated base */}
            <Text className="font-bold mb-2">{t('TendencyReport.Sources.CuratedBase')}</Text>
            <Text className="text-xs text-gray-500 mb-2">{t('TendencyReport.Sources.SearchHint')}</Text>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-6">
                {curated.map((s) => (
                    <label key={s.url} className="flex items-start gap-2 border border-gray-200 rounded-md px-3 py-2 cursor-pointer hover:bg-gray-50">
                        <input type="checkbox" className="mt-1" checked={selected.includes(s.url)} onChange={() => toggle(s.url)} />
                        <span className="text-sm">
                            <span className="font-medium block">{s.name}</span>
                            <a href={s.url} target="_blank" rel="noreferrer" className="text-iavisus-100 text-xs break-all" onClick={(e) => e.stopPropagation()}>
                                {s.url}
                            </a>
                        </span>
                    </label>
                ))}
            </div>

            <div className="flex items-center gap-3">
                <Button onClick={save} loading={saving} className="bg-iavisus-900">
                    {t('TendencyReport.Sources.Save')}
                </Button>
                {savedMsg && <Text className="text-green-600">{t('TendencyReport.Sources.Saved')}</Text>}
            </div>
        </div>
    );
}

export default TrSourcesTab;
